import { useDropdown } from '@calendly/ui/components/dropdown'
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon } from '@calendly/ui/components/icons'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'

import { Calendar } from 'components/calendar'

import { rangeLength } from '../utils'
import { Button, Container, Menu, Wrapper } from './styles'
import { formatDateTitle } from './utils'

const COMPONENT_ID = 'calendar-picker-popup'

const getAvailableDays = (currentMonth, timezone) => {
  const month = moment.tz(currentMonth, 'YYYY-MM', timezone)
  const today = moment().tz(timezone).startOf('day')

  return Object.fromEntries(Array.from({ length: month.daysInMonth() }, (_, i) => {
    const currentDay = month.clone().add(i, 'days')

    if (currentDay < today) return [currentDay.format('YYYY-MM-DD'), []]

    return [currentDay.format('YYYY-MM-DD'), [true]]
  }))
}

const getCurrentRangeStart = ({ date, timezone }) => (
  date.isBefore(moment().startOf('day')) ? moment().tz(timezone).startOf('day') : date
)

export const CalendarPickerDropdown = ({
  date,
  goToDate,
  isPhone,
  locale,
  timezone,
  currentView,
  monthFormat = null,
  monthComponent: MonthComponent = 'span'
}) => {
  const [currentRangeStart, setCurrentRangeStart] = useState(getCurrentRangeStart({ date, timezone }))
  const [toggleDropdown, Dropdown, isOpen] = useDropdown()
  const [dateTitle, setDateTitle] = useState('')
  const currentMonth = moment.tz(currentRangeStart, timezone).format('YYYY-MM')
  const [currentMonthId, setCurrentMonthId] = useState(currentMonth)
  const [selectedDate, setSelectedDate] = useState(currentRangeStart.format('YYYY-MM-DD'))
  const [isLoading, setIsLoading] = useState(false)


  const changeMonth = (month) => {
    setCurrentMonthId(month)
  }

  const availableDays = getAvailableDays(currentMonthId, timezone)

  const onDayClick = (clickedDate) => {
    setSelectedDate(clickedDate)
    setIsLoading(true)
  }

  useEffect(() => {
    setCurrentRangeStart(getCurrentRangeStart({ date, timezone }))
  }, [date, timezone])

  useEffect(() => {
    if (isLoading) {
      goToDate(selectedDate)
      setIsLoading(false)
      toggleDropdown()
    }
  }, [goToDate, isLoading, selectedDate, toggleDropdown])

  useEffect(() => {
    const daysRange = rangeLength(currentView)

    setCurrentMonthId(currentMonth)
    setDateTitle(formatDateTitle(currentRangeStart, isPhone, locale, daysRange, { monthFormat }))
  }, [currentRangeStart, locale, isPhone, currentMonth, monthFormat, currentView])


  return (
    <Container>
      <Button
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={isOpen ? COMPONENT_ID : null}
        onClick={toggleDropdown}
        expanded={isOpen}
      >
        <MonthComponent>{dateTitle}</MonthComponent>
        {isOpen ? <ArrowChevronUpMiniIcon size="large" /> : <ArrowChevronDownMiniIcon size="large" />}
      </Button>
      <Dropdown>
        <Menu id={COMPONENT_ID} data-component={COMPONENT_ID}>
          <Wrapper>
            <Calendar
              availableDays={availableDays}
              selectedSpots={[]}
              selectedDay=""
              onDayClick={onDayClick}
              isDaysLoading={isLoading}
              timezone={timezone}
              currentMonthId={currentMonthId}
              setCurrentMonthId={changeMonth}
              showSubscript={false}
              stylePrimary={false}
              pastDisabled
              showSurroundingMonths
            />
          </Wrapper>
        </Menu>
      </Dropdown>
    </Container>
  )
}
